import logger from '@eneco/common/log';
import * as SparkyIllustrations from '@eneco/sparky/illustrations';
import { Placeholder } from '@eneco/sparky/illustrations';

type IllustrationComponent = typeof Placeholder;

/**
 * Resolve a CMS illustration name to its design-system component.
 *
 * Nothing here is CMS-specific: it is a lookup in Sparky's own exports. It
 * lives in this library only because the module boundaries leave nowhere
 * shared to put it. The same function already exists twice, in
 * `@sitecore/common`'s `IllustrationHandler` and in
 * `libs/cms-components/src/illustrations.ts`, and flows may depend on neither:
 * `lib:sitecore-common` is what this story removes, and `lib:cms-components` is
 * not in `lib:flows`'s allow-list.
 *
 * This is therefore a third copy, deliberately and temporarily.
 * `lib:custom-components` is the one tag that `lib:flows` and
 * `lib:cms-components` both allow, so consolidating all three there is the
 * fix; it touches two libraries outside this story's scope and is tracked
 * separately.
 *
 * The name is typed as a plain string rather than the `Illustrations` union
 * from `@sitecore/types`. The union was never a guarantee, since the value
 * arrives as CMS content and every call site cast to it anyway; an unknown name
 * logs and falls back to Sparky's own `Placeholder`, so a dropped illustration
 * shows as a visible gap rather than silently blank.
 */
export const getIllustration = (name: string | undefined): IllustrationComponent | undefined => {
  if (!name) return undefined;
  if (name in SparkyIllustrations) {
    return (SparkyIllustrations as Record<string, IllustrationComponent>)[name];
  }
  logger.error('wVRxzv', `Illustration ${name} could not be found. A placeholder will be rendered.`);
  return Placeholder;
};
